import React from 'react';
import { Box, IconButton, InputLabel, MenuItem, Typography } from '@mui/material';
import { useLocales, useTheme } from '../../../../hooks';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller, useFieldArray } from 'react-hook-form';
import { markAsRequired } from '../../../../utils/formHelpers';
import FormControl from '../../FormControl';
import Select from '../../Select';
import FoldableSection from '../../FoldableSection';
import { getContentGroupOptions } from '../../../../utils/contentService';
import Button from '../../Button';
import { ContentGroupsType } from '../../../../API';
import CountryPickerLite from '../../CountryPickerLite';
import { Sortable } from '../../Sortable';
import { Add, Delete } from '@mui/icons-material';
import { enrichTestIds } from '../../TestsIds';
import { EnrichForm } from './Enrich';

interface ContentEntitlementFilteringProps {
  isSaving: boolean;
  control: Control<EnrichForm>;
}
export const useStyles = makeStyles()((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    margin: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  selector: {
    minWidth: 360
  },
  countryPicker: {
    minWidth: 450,
    flexGrow: 1
  },
  dynamicFormRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    verticalAlign: 'middle'
  },
  dynamicFormContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(2)
  },
  dynamicFormAddRow: {
    alignSelf: 'flex-start'
  }
}));

export function ContentEntitlementFiltering({ isSaving, control }: ContentEntitlementFilteringProps): JSX.Element {
  const { classes } = useStyles();
  const { formControlColor } = useTheme();
  const { t } = useLocales();

  const {
    append: appendContentEntitlementFiltering,
    remove: removeContentEntitlementFiltering,
    fields: fieldsContentEntitlementFiltering,
    replace: replaceContentEntitlementFiltering
  } = useFieldArray({ control, name: 'contentEntitlementFiltering' });

  const onAddContentEntitlementFiltering = () => {
    appendContentEntitlementFiltering({
      contentGroup: [],
      countryCodes: []
    });
  };

  return (
    <FoldableSection
      summary={<Typography variant="h6">{t('content.drawer.content_entitlement_filtering.foldable_title')}</Typography>}
      disableGutters
      expanded={fieldsContentEntitlementFiltering.length > 0}
    >
      <div className={classes.dynamicFormContainer}>
        <Sortable
          list={fieldsContentEntitlementFiltering}
          setList={replaceContentEntitlementFiltering}
          ghostClass="sortableGhost"
          dragClass="sortableDragDefault"
        >
          {fieldsContentEntitlementFiltering.map((contentEntitlementFiltering, i) => (
            <Box key={contentEntitlementFiltering.id} className={classes.form}>
              <div className={classes.dynamicFormRow}>
                <Controller
                  control={control}
                  name={`contentEntitlementFiltering.${i}.contentGroup`}
                  rules={{ required: t('general.field_is_required') }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <FormControl>
                      <InputLabel id="contentGroupSelector">
                        {markAsRequired(t('content.drawer.content_entitlement_filtering.content_group'), true)}
                      </InputLabel>
                      <Select
                        label={markAsRequired(t('content.drawer.content_entitlement_filtering.content_group'), true)}
                        className={classes.selector}
                        required
                        labelId="contentGroupSelector"
                        value={value}
                        onChange={({ target: { value } }) => onChange(value as unknown as ContentGroupsType[])}
                        data-testid={enrichTestIds.contentGroupSelector}
                        error={!!error}
                        multiple
                        disabled={isSaving}
                      >
                        {getContentGroupOptions().map(({ value, label }) => (
                          <MenuItem
                            key={value}
                            value={value}
                            data-testid={enrichTestIds.contentGroupSelectorItem}
                            data-treatment-id={value}
                          >
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  control={control}
                  name={`contentEntitlementFiltering.${i}.countryCodes`}
                  rules={{ required: t('general.field_is_required') }}
                  render={({ field: { value, onChange } }) => (
                    <FormControl>
                      <CountryPickerLite
                        label={markAsRequired(t('media.countries'), true)}
                        className={classes.countryPicker}
                        value={value || []}
                        onChange={onChange}
                        required
                        disabled={isSaving}
                      />
                    </FormControl>
                  )}
                />
                <IconButton
                  title={t('content.drawer.content_entitlement_filtering.actions.remove_content_entitlement_filtering')}
                  onClick={() => removeContentEntitlementFiltering(i)}
                  sx={{ mt: 4 }}
                  data-testid={enrichTestIds.contentEntitlementFilteringRemoveButton}
                  disabled={isSaving}
                >
                  <Delete />
                </IconButton>
              </div>
            </Box>
          ))}
        </Sortable>
        <Button
          startIcon={<Add />}
          color={formControlColor}
          variant="outlined"
          onClick={onAddContentEntitlementFiltering}
          data-testid={enrichTestIds.contentEntitlementFilteringAddButton}
          className={classes.dynamicFormAddRow}
          disabled={isSaving}
        >
          {t('content.drawer.content_entitlement_filtering.actions.add_content_entitlement_filtering')}
        </Button>
      </div>
    </FoldableSection>
  );
}
